import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { API } from '@/api';
import { API_ENDPOINTS, useApiRequest } from '@/hooks/useApiRequest';

type TimeSlot = API['GET']['ShowerTimeSlots']['response']['time_slots'][number];

interface GroupedTimeSlot {
  date: string;
  hours: Array<{
    hour: string;
    slots: TimeSlot[];
  }>;
}

/**
 * 時間スロットのデータ取得とグループ化を行うカスタムフック
 * @returns 時間スロットデータとエラー状態
 */
export const useTimeSlots = () => {
  const { t } = useTranslation();
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const { isRequesting, execute } = useApiRequest(
    'GET',
    API_ENDPOINTS.GET.SHOWER_TIME_SLOTS,
  );

  // 時間帯を取得
  useEffect(() => {
    const fetchTimeSlots = async () => {
      const result = await execute({});

      if (result.ok) {
        setTimeSlots(result.data.time_slots);
        setError(null);
      } else {
        setError(t('shareDevice.timeSelection.error'));
        console.error('Failed to fetch time slots:', result.error);
      }
      setHasLoaded(true);
    };

    fetchTimeSlots();
  }, [execute, t]);

  // 日付ごとにグループ化し、さらに時間ごとにグループ化
  // displayable=falseのスロットのみの時間帯は除外
  const groupedTimeSlots: GroupedTimeSlot[] = (() => {
    const groupedByDate = new Map<string, Map<string, TimeSlot[]>>();

    timeSlots.forEach((slot) => {
      const date = slot.date;
      const hour = slot.time.split(':')[0];

      if (!groupedByDate.has(date)) {
        groupedByDate.set(date, new Map());
      }

      const dateGroup = groupedByDate.get(date)!;
      const hourSlots = dateGroup.get(hour) ?? [];
      hourSlots.push(slot);
      dateGroup.set(hour, hourSlots);
    });

    return Array.from(groupedByDate, ([date, hoursMap]) => ({
      date,
      hours: Array.from(hoursMap, ([hour, slots]) => ({
        hour,
        slots,
      })).filter(({ slots }) =>
        // displayable=trueのスロットが1つでもあれば、その時間帯を表示
        slots.some((slot) => slot.displayable !== false),
      ),
    })).filter((dateGroup) => dateGroup.hours.length > 0); // 時間帯が1つもない日付も除外
  })();

  // 営業時間内のスロットが存在するかどうか
  const hasDisplayableSlots = timeSlots.some(
    (slot) => slot.displayable !== false,
  );

  return {
    groupedTimeSlots,
    isRequesting,
    error,
    hasLoaded,
    hasDisplayableSlots,
  };
};
